import React, { useState, useEffect, useCallback, useContext } from 'react';
import './SearchProgress.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  useTheme,
  Button,
  Collapse,
  Tooltip,
  Popover
} from '@mui/material';
import {
  CheckCircle,
  Error,
  PendingOutlined,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  ThumbUp,
  ThumbDown,
  ChatBubbleOutline,
  NavigateBeforeRounded,
  NavigateNextRounded,
  ChevronRight,
  ThumbUpAlt,
  Science,
  Psychology,
  Search,
  FilterList,
  AutoAwesome,
  AccessTime,
  FindInPage,
  ErrorOutline
} from '@mui/icons-material';
import { useThemeContext } from '../../contexts/ThemeContext'; // Fix import to use useThemeContext

const SearchProgress = ({
  searchSteps,
  rankingResults = [],
  isLoading,
  onToggleExpand
}) => {
  const { isDarkMode } = useThemeContext(); // Use the hook instead of context directly
  const theme = useTheme();
  const [activeRankings, setActiveRankings] = useState([0]);
  const [mainSectionExpanded, setMainSectionExpanded] = useState(true);
  const [resultsVisible, setResultsVisible] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3;

  // Add state for the details popover
  const [detailsPopoverAnchor, setDetailsPopoverAnchor] = useState(null);
  const [activeStepDetails, setActiveStepDetails] = useState(null);

  // Function to handle opening the details popover
  const handleOpenDetailsPopover = (event, step) => {
    event.stopPropagation();
    setDetailsPopoverAnchor(event.currentTarget);
    setActiveStepDetails(step);
  };

  // Function to handle closing the details popover
  const handleCloseDetailsPopover = () => {
    setDetailsPopoverAnchor(null);
    setActiveStepDetails(null);
  };

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    if (timestamp instanceof Date) {
      return timestamp.toLocaleTimeString();
    }
    return timestamp;
  };

  // Add a new utility function to format timestamps with milliseconds
  const formatTimestampPrecise = (timestamp) => {
    if (!timestamp) return '';
    if (timestamp instanceof Date) {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
    }
    return timestamp;
  };

  // Add a utility function to calculate duration between timestamps
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;

    // Convert to Date objects if they aren't already
    const start = startTime instanceof Date ? startTime : new Date(startTime);
    const end = endTime instanceof Date ? endTime : new Date(endTime);

    // Calculate the difference in milliseconds
    const diffMs = end - start;

    // Format the duration based on its length
    if (diffMs < 1000) {
      return `${diffMs}ms`;
    } else if (diffMs < 60000) {
      return `${(diffMs / 1000).toFixed(2)}s`;
    } else {
      const minutes = Math.floor(diffMs / 60000);
      const seconds = ((diffMs % 60000) / 1000).toFixed(1);
      return `${minutes}m ${seconds}s`;
    }
  };

  // Helper to properly format the score for display
  const formatScore = (score) => {
    // Ensure score is a number and convert to percentage if needed
    if (score === undefined || score === null) return 0;

    // If score is already a percentage (0-100), return as is
    if (score >= 0 && score <= 100) return score;

    // If score is a fraction (0-1), convert to percentage
    if (score >= 0 && score <= 1) return Math.round(score * 100);

    // Default fallback
    return Math.round(score);
  };

  // Add debugging information at the top of the component
  useEffect(() => {

    // Always make sure we have active rankings when there are results
    if (rankingResults && rankingResults.length > 0) {
      setResultsVisible(true);

      // Ensure first result is expanded
      if (!activeRankings.includes(0)) {
        setActiveRankings(prev => [...prev, 0]);
      }
    }
  }, [rankingResults, searchSteps, isLoading]);

  useEffect(() => {
    // Automatically animate in ranking results with a staggered delay
    if (rankingResults && rankingResults.length > 0) {
      const timer = setTimeout(() => {
        const newActiveRankings = [];
        const staggerInterval = 150; // ms between each item appearing

        rankingResults.forEach((_, index) => {
          setTimeout(() => {
            setActiveRankings(prev => [...prev, index]);
          }, index * staggerInterval);
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [rankingResults]);

  // Update the useEffect to handle search completion - making it collapse by default on component load
  useEffect(() => {
    // Check if search is complete by verifying all steps are completed
    const isSearchComplete = searchSteps.length > 0 &&
      searchSteps.every(step => step.completed);

    if (isSearchComplete) {
      // Collapse immediately when complete
      setMainSectionExpanded(false);
    }
  }, [searchSteps]); // Only depend on searchSteps, not rankingResults

  // Extract the paper title with fallback options
  const extractPaperTitle = (paper) => {
    if (!paper) return 'Untitled Paper';
    // Try to extract from title, then paperId
    return paper.title || `Paper ${paper.paperId || ''}`;
  };

  // Format the arguments text to improve readability
  const formatArgumentText = (text) => {
    if (!text) return '';

    // Remove the 'Extracted Sentences:' part from display
    return text.replace(/Extracted Sentences: "[^"]+"/g, '');
  };

  // Extract arguments for a paper
  const extractArguments = (paper) => {
    if (!paper) return '';


    // Handle different data structures
    if (paper.reasoning && typeof paper.reasoning.argumentsFor === 'string') {
      return formatArgumentText(paper.reasoning.argumentsFor);
    } else if (typeof paper.argumentsFor === 'string') {
      return formatArgumentText(paper.argumentsFor);
    }

    return '';
  };

  const extractArgumentsAgainst = (paper) => {
    if (!paper) return '';


    // Handle different data structures
    if (paper.reasoning && typeof paper.reasoning.argumentsAgainst === 'string') {
      return formatArgumentText(paper.reasoning.argumentsAgainst);
    } else if (typeof paper.argumentsAgainst === 'string') {
      return formatArgumentText(paper.argumentsAgainst);
    }

    return '';
  };

  const getRelevantQuotes = (result) => {
    if (!result) return [];

    // Extract quotes from argumentsFor and argumentsAgainst if they contain the pattern
    const quotes = [];

    // Try to extract from reasoning structure first
    if (result.reasoning) {
      const forText = result.reasoning.argumentsFor || '';
      const againstText = result.reasoning.argumentsAgainst || '';

      // Extract quotes using regex pattern 'Extracted Sentences: "[quote]"'
      const forMatch = forText.match(/Extracted Sentences: "([^"]+)"/);
      const againstMatch = againstText.match(/Extracted Sentences: "([^"]+)"/);

      if (forMatch && forMatch[1]) quotes.push(forMatch[1]);
      if (againstMatch && againstMatch[1]) quotes.push(againstMatch[1]);
    }

    // If we found quotes from the reasoning field, return them
    if (quotes.length > 0) return quotes;

    // Otherwise try other properties
    if (Array.isArray(result.relevantQuotes)) return result.relevantQuotes;
    if (Array.isArray(result.quotes)) return result.quotes;
    if (Array.isArray(result.keyQuotes)) return result.keyQuotes;

    return [];
  };

  const toggleMainSection = () => {
    setMainSectionExpanded(!mainSectionExpanded);
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  const toggleResultExpand = (index) => {
    setActiveRankings(prev => {
      const newActiveRankings = [...prev];
      const currentIndex = newActiveRankings.indexOf(index);
      if (currentIndex !== -1) {
        newActiveRankings.splice(currentIndex, 1);
      } else {
        newActiveRankings.push(index);
      }
      return newActiveRankings;
    });
  };

  const toggleResultsSection = () => {
    setResultsVisible(!resultsVisible);
  };

  // Process the current page items
  const getCurrentPageItems = useCallback(() => {
    if (!rankingResults || rankingResults.length === 0) return [];

    const startIdx = currentPage * itemsPerPage;
    const endIdx = Math.min(startIdx + itemsPerPage, rankingResults.length);

    const pageItems = rankingResults.slice(startIdx, endIdx);
    return pageItems;
  }, [rankingResults, currentPage, itemsPerPage]);

  // Navigation functions for pagination
  const goToNextPage = () => {
    const totalResults = rankingResults.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get current page items


  const getStatusIcon = (step) => {
    if (step.completed) {
      return <CheckCircle sx={{ color: '#4caf50', fontSize: '0.9rem', animation: 'none' }} />;
    } else if (step.error) {
      return <Error sx={{ color: '#f44336', fontSize: '0.9rem', animation: 'none' }} />;
    } else {
      return <PendingOutlined sx={{ color: '#2196f3', fontSize: '0.9rem', animation: 'pending-pulse 1.5s infinite' }} />;
    }
  };

  const getScoreColor = (score) => {
    // Ensure score is a number and between 0-100
    const numScore = formatScore(score);

    if (numScore >= 80) return theme.palette.success.main;
    if (numScore >= 60) return theme.palette.info.main;
    if (numScore >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getProgressVariant = (step, index, total) => {
    // Completed steps
    if (step.completed) return 'completed';
    // Error steps
    if (step.error) return 'error';
    // The current pending step
    if (!step.completed && step.processingIndex === true) return 'current';
    // Future steps
    return 'future';
  };

  // Calculate completion percentage
  const completedSteps = searchSteps.filter(step => step.completed).length;
  const totalSteps = searchSteps.length;
  const progressPercentage = totalSteps > 0 ? Math.floor((completedSteps / totalSteps) * 100) : 0;

  // Motion variants for animations - updating for smoother animations
  const itemVariants = {
    hidden: { opacity: 0, y: -3, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, y: 3, scale: 0.98, transition: { duration: 0.2, ease: "easeIn" } }
  };

  // Animation for active step
  const pulseAnimation = {
    animate: {
      scale: [1, 1.05, 1],
      opacity: [1, 0.9, 1],
      transition: {
        duration: 2,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse"
      }
    }
  };

  // Apply modern glass-morphism styling with improved aesthetics - more compact
  const cardStyle = {
    background: isDarkMode
      ? 'rgba(30, 30, 40, 0.7)'
      : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(10px)',
    boxShadow: isDarkMode
      ? '0 8px 24px rgba(0, 0, 0, 0.2)'
      : '0 8px 24px rgba(100, 100, 255, 0.08)',
    border: isDarkMode
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(200, 200, 255, 0.2)',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.3s ease-in-out',
    marginBottom: '1rem',
  };

  // Helper function to extract technical details from steps
  const getStepTechnicalDetails = (step) => {
    if (!step || !step.details) return null;

    // Extract any technical information based on step type
    if (step.message.includes('Initializing')) {
      return {
        details: 'Preparing search infrastructure and parameters'
      };
    } else if (step.message.includes('Generating optimized')) {
      return {
        details: step.details || 'Using NLP to transform your query for academic search engines'
      };
    } else if (step.message.includes('Searching')) {
      return {
        details: step.details || 'Querying multiple academic databases in parallel'
      };
    } else if (step.message.includes('Found')) {
      const paperCount = step.message.match(/Found (\d+) papers/);
      return {
        icon: <FilterList sx={{ fontSize: '0.9rem' }} />,
        details: paperCount ? `${paperCount[1]} papers retrieved from databases` : 'Results retrieved successfully'
      };
    } else if (step.message.includes('Analyzing')) {
      return {
        icon: <AutoAwesome sx={{ fontSize: '0.9rem' }} />,
        details: step.details || 'AI is evaluating paper relevance based on your criteria'
      };
    } else if (step.error) {
      return {
        icon: <ErrorOutline sx={{ fontSize: '0.9rem' }} />,
        details: `Error: ${step.errorDetails || 'See console for details'}`
      };
    }

    return null;
  };

  // Add a utility function to generate step performance indicators
  const getStepPerformanceIndicator = (step, prevStep) => {
    if (!step.timestamp || !prevStep?.timestamp || !step.completed) return null;

    const duration = calculateDuration(prevStep.timestamp, step.timestamp);

    let performanceRating = null;
    let color = 'inherit';

    // Performance thresholds will depend on the step type
    if (step.message.includes('Generating optimized')) {
      // Query generation should be relatively fast
      const durationMs = new Date(step.timestamp) - new Date(prevStep.timestamp);
      if (durationMs < 2000) {
        performanceRating = 'fast';
        color = '#4caf50';
      } else if (durationMs < 5000) {
        performanceRating = 'normal';
        color = '#2196f3';
      } else {
        performanceRating = 'slow';
        color = '#ff9800';
      }
    } else if (step.message.includes('Found')) {
      // Search should complete within reasonable time
      const durationMs = new Date(step.timestamp) - new Date(prevStep.timestamp);
      if (durationMs < 5000) {
        performanceRating = 'fast';
        color = '#4caf50';
      } else if (durationMs < 15000) {
        performanceRating = 'normal';
        color = '#2196f3';
      } else {
        performanceRating = 'slow';
        color = '#ff9800';
      }
    } else if (step.message.includes('Analyzing')) {
      // Analysis can take longer
      const durationMs = new Date(step.timestamp) - new Date(prevStep.timestamp);
      if (durationMs < 10000) {
        performanceRating = 'fast';
        color = '#4caf50';
      } else if (durationMs < 30000) {
        performanceRating = 'normal';
        color = '#2196f3';
      } else {
        performanceRating = 'slow';
        color = '#ff9800';
      }
    }

    if (performanceRating) {
      return (
        <Tooltip title={`Processing time: ${duration}`}>
          <Chip
            size="small"
            label={duration}
            sx={{
              height: '16px',
              fontSize: '0.65rem',
              color: color,
              backgroundColor: isDarkMode ? 'rgba(30, 30, 40, 0.6)' : 'rgba(255, 255, 255, 0.7)',
              ml: 1
            }}
          />
        </Tooltip>
      );
    }

    return null;
  };

  // Helper function to get step status details popup content
  const getStepStatusDetails = (step) => {
    if (!step) return null;

    return (
      <Box sx={{ p: 1, maxWidth: '280px' }}>
        {step.completed && (
          <Typography variant="caption" sx={{ display: 'block', color: '#4caf50', mb: 0.5 }}>
            ✓ Completed successfully
          </Typography>
        )}

        {step.error && (
          <Typography variant="caption" sx={{ display: 'block', color: '#f44336', mb: 0.5 }}>
            ✗ Error encountered
          </Typography>
        )}

        {step.timestamp && (
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mb: 0.5 }}>
            Started at: {formatTimestampPrecise(step.timestamp)}
          </Typography>
        )}

        {step.endTimestamp && (
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mb: 0.5 }}>
            Completed at: {formatTimestampPrecise(step.endTimestamp)}
          </Typography>
        )}

        {step.details && (
          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
            {step.details}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="search-progress-component"
    >
      <Card
        className="search-progress-card"
        sx={{
          ...cardStyle,
          "& .search-steps-timeline .timeline-line": {
            position: "absolute",
            left: 10,
            width: 2,
            zIndex: 0,
            transition: "background-color 0.3s ease",
          },
          "& .search-steps-timeline .step-circle": {
            width: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            zIndex: 2,
            position: "relative",
          },
          "& .search-steps-timeline .timeline-line.completed": {
            backgroundColor: "#4caf50", // Green for completed
          },
          "& .search-steps-timeline .timeline-line.error": {
            backgroundColor: "#f44336", // Red for error
          },
          "& .search-steps-timeline .timeline-line.active": {
            backgroundColor: "#2196f3", // Blue for active
          },
          "& .search-step-item": {
            position: "relative",
            minHeight: 36, // Minimum height for consistent spacing
          }
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  Search Progress
                </Typography>
                <Chip
                  label={`${progressPercentage}% • ${completedSteps}/${totalSteps}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    height: '22px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderRadius: '11px',
                    background: isDarkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.08)',
                  }}
                />
              </Box>
              <IconButton
                onClick={toggleMainSection}
                size="small"
                sx={{
                  width: '28px',
                  height: '28px',
                  transition: 'transform 0.3s ease, background 0.2s ease',
                  transform: mainSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  }
                }}
              >
                {mainSectionExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
          }
          sx={{
            bgcolor: isDarkMode ? 'rgba(30, 30, 40, 0.7)' : 'rgba(250, 250, 250, 0.7)',
            borderBottom: mainSectionExpanded ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}` : 'none',
            cursor: 'pointer',
            padding: '12px 16px',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(40, 40, 50, 0.8)' : 'rgba(245, 245, 245, 0.8)'
            }
          }}
          onClick={toggleMainSection}
        />
        <Collapse in={mainSectionExpanded}>
          <CardContent sx={{ p: '12px 16px 16px' }}>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                mb: 2,
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${isDarkMode ? '#2979ff' : '#2196f3'}, ${isDarkMode ? '#82b1ff' : '#64b5f6'})`,
                  borderRadius: 3,
                  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }
              }}
            />

            {/* Timeline-style steps list */}
            <Box className="search-steps-timeline" sx={{ position: 'relative', mb: 2, ml: 1.5 }}>
              {/* Static timeline background line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 10,
                  top: 10,
                  bottom: 0,
                  width: 2,
                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  zIndex: 0,
                }}
              />

              <AnimatePresence>
                {searchSteps.map((step, index) => {
                  // Determine if this is the last step
                  const isLastStep = index === searchSteps.length - 1;
                  const nextStep = index < searchSteps.length - 1 ? searchSteps[index + 1] : null;

                  // Determine if this is the active step (not completed but previous step is)
                  const isActive = !step.completed && !step.error &&
                    (index === 0 || (index > 0 && searchSteps[index - 1].completed));

                  // Status classes for styling
                  const statusClass = step.completed ? 'completed' : (step.error ? 'error' : (isActive ? 'active' : ''));

                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      transition={{ delay: index * 0.07 }}
                    >
                      <Box
                        className={`search-step-item ${statusClass}`}
                        sx={{
                          display: 'flex',
                          position: 'relative',
                          mb: isLastStep ? 0 : 2.5, // Increased spacing between steps
                          pl: 3.5,
                          pr: 1,
                          py: 0.5,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {/* Colored connecting line to next step */}
                        {!isLastStep && (
                          <Box
                            className={`timeline-line ${statusClass}`}
                            sx={{
                              top: 10,
                              height: 48, // Increase height to ensure overlap
                              background: step.completed
                                ? '#4caf50'
                                : step.error
                                  ? '#f44336'
                                  : isActive
                                    ? '#2196f3'
                                    : isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                            }}
                          />
                        )}

                        {/* Circle indicator */}
                        <motion.div
                          animate={isActive ? pulseAnimation.animate : {}}
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            zIndex: 5, // Increased z-index to ensure circle is above line
                          }}
                        >
                          <Box
                            className={`step-circle ${statusClass}`}
                            sx={{
                              backgroundColor: isDarkMode
                                ? 'rgba(30, 30, 40, 0.9)'
                                : 'rgba(255, 255, 255, 0.9)',
                              border: `2px solid ${step.completed
                                ? '#4caf50'
                                : step.error
                                  ? '#f44336'
                                  : '#2196f3'
                                }`,
                              boxShadow: isActive
                                ? '0 0 0 4px rgba(33, 150, 243, 0.15)'
                                : '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {getStatusIcon(step)}
                          </Box>
                        </motion.div>

                        {/* Step content */}
                        <Box
                          sx={{
                            flexGrow: 1,
                            pl: 0.5,
                            opacity: step.completed || step.error || isActive ? 1 : 0.7
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isActive ? 700 : 600,
                              fontSize: '0.875rem',
                              lineHeight: 1.4,
                              color: isDarkMode
                                ? step.completed
                                  ? 'rgba(255, 255, 255, 0.95)'
                                  : step.error
                                    ? '#ff8a80'
                                    : isActive
                                      ? '#90caf9'
                                      : 'rgba(255, 255, 255, 0.9)'
                                : step.completed
                                  ? 'rgba(0, 0, 0, 0.85)'
                                  : step.error
                                    ? '#d32f2f'
                                    : isActive
                                      ? '#1976d2'
                                      : 'rgba(0, 0, 0, 0.8)'
                            }}
                          >
                            {step.message}
                            {index > 0 && getStepPerformanceIndicator(step, searchSteps[index - 1])}
                          </Typography>

                          {/* Improved timestamp with additional technical details */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                            {step.timestamp && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isDarkMode
                                    ? step.completed
                                      ? 'rgba(76, 175, 80, 0.6)'
                                      : step.error
                                        ? 'rgba(244, 67, 54, 0.6)'
                                        : isActive
                                          ? 'rgba(33, 150, 243, 0.7)'
                                          : 'rgba(255, 255, 255, 0.5)'
                                    : step.completed
                                      ? 'rgba(76, 175, 80, 0.7)'
                                      : step.error
                                        ? 'rgba(244, 67, 54, 0.7)'
                                        : isActive
                                          ? 'rgba(33, 150, 243, 0.8)'
                                          : 'rgba(0, 0, 0, 0.5)',
                                  display: 'block',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {formatTimestamp(step.timestamp)}
                              </Typography>
                            )}

                            {/* Show technical details badge if available */}
                            {getStepTechnicalDetails(step) && (
                              <Tooltip title={getStepTechnicalDetails(step).details}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    ml: 1,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '12px',
                                    px: 0.7,
                                    py: 0.2
                                  }}
                                >
                                  {getStepTechnicalDetails(step).icon}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      ml: 0.3,
                                      fontSize: '0.65rem',
                                      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                    }}
                                  >
                                    {getStepTechnicalDetails(step).details.split(' ')[0]}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        {/* Details button (if available) */}
                        {(step.details || step.error) && (
                          <Tooltip title={step.error ? "View error details" : "View details"}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenDetailsPopover(e, step)}
                              sx={{
                                width: '22px',
                                height: '22px',
                                ml: 0.5,
                                background: isDarkMode
                                  ? step.error ? 'rgba(244, 67, 54, 0.15)' : 'rgba(255, 255, 255, 0.05)'
                                  : step.error ? 'rgba(244, 67, 54, 0.1)' : 'rgba(0, 0, 0, 0.03)',
                                '&:hover': {
                                  background: isDarkMode
                                    ? step.error ? 'rgba(244, 67, 54, 0.25)' : 'rgba(255, 255, 255, 0.1)'
                                    : step.error ? 'rgba(244, 67, 54, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 2px 5px rgba(0,0,0,0.15)'
                                },
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: '-4px',
                                  right: '-4px',
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: step.error ? '#f44336' : '#2196f3',
                                  opacity: 0.7,
                                  animation: step.error ? 'pulse-error 2s infinite' : 'pulse-info 2s infinite'
                                },
                                '@keyframes pulse-info': {
                                  '0%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.4)' },
                                  '70%': { boxShadow: '0 0 0 5px rgba(33, 150, 243, 0)' },
                                  '100%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)' }
                                },
                                '@keyframes pulse-error': {
                                  '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
                                  '70%': { boxShadow: '0 0 0 5px rgba(244, 67, 54, 0)' },
                                  '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' }
                                }
                              }}
                            >
                              {step.error
                                ? <ErrorOutline sx={{ fontSize: '0.85rem', color: '#f44336' }} />
                                : <ChatBubbleOutline sx={{ fontSize: '0.85rem', color: theme.palette.primary.main }} />
                              }
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </Box>

            {/* Details Popover */}
            <Popover
              open={!!detailsPopoverAnchor}
              anchorEl={detailsPopoverAnchor}
              onClose={handleCloseDetailsPopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  maxWidth: '350px',
                  mt: 1,
                  overflow: 'hidden',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                  bgcolor: isDarkMode ? 'rgba(30, 30, 40, 0.95)' : 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)'
                }
              }}
            >
              {activeStepDetails && (
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: activeStepDetails.error
                        ? '#f44336'
                        : activeStepDetails.completed
                          ? '#4caf50'
                          : '#2196f3'
                    }}
                  >
                    {activeStepDetails.message}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 500 }}>
                        Status:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: activeStepDetails.error
                          ? '#f44336'
                          : activeStepDetails.completed
                            ? '#4caf50'
                            : '#2196f3'
                      }}>
                        {activeStepDetails.completed && !activeStepDetails.error && (
                          <><CheckCircle fontSize="inherit" sx={{ mr: 0.5 }} /> Completed</>
                        )}
                        {activeStepDetails.error && (
                          <><Error fontSize="inherit" sx={{ mr: 0.5 }} /> Error</>
                        )}
                        {!activeStepDetails.completed && !activeStepDetails.error && (
                          <><PendingOutlined fontSize="inherit" sx={{ mr: 0.5 }} /> Processing</>
                        )}
                      </Typography>
                    </Grid>

                    {activeStepDetails.timestamp && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 500 }}>
                            Started:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            {formatTimestampPrecise(activeStepDetails.timestamp)}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {activeStepDetails.endTimestamp && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 500 }}>
                            Completed:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            {formatTimestampPrecise(activeStepDetails.endTimestamp)}
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 500 }}>
                            Duration:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            {calculateDuration(activeStepDetails.timestamp, activeStepDetails.endTimestamp)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {activeStepDetails.details && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 500, mb: 0.5 }}>
                        Details:
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          bgcolor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {activeStepDetails.details}
                        </Typography>
                      </Paper>
                    </>
                  )}

                  {activeStepDetails.error && activeStepDetails.errorDetails && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 500, mb: 0.5 }}>
                        Error Details:
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                          border: '1px solid rgba(244, 67, 54, 0.2)',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>
                          {activeStepDetails.errorDetails}
                        </Typography>
                      </Paper>
                    </>
                  )}
                </Box>
              )}
            </Popover>

            {(rankingResults && rankingResults.length > 0) && (
              <Box className="ranking-results" sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1.5,
                    p: '8px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    bgcolor: isDarkMode ? 'rgba(30, 30, 40, 0.7)' : 'rgba(250, 250, 250, 0.7)',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(40, 40, 50, 0.8)' : 'rgba(245, 245, 245, 0.8)',
                      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.04)'
                    }
                  }}
                  onClick={toggleResultsSection}
                >
                  <Box
                    sx={{
                      mr: 1,
                      display: 'flex',
                      transform: resultsVisible ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <ChevronRight fontSize="small" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ThumbUpAlt sx={{ mr: 1, fontSize: '0.9rem', color: theme.palette.primary.main }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                      AI-Ranked Results ({rankingResults.length})
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                    ml: 1.5,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                    fontStyle: 'italic',
                    fontSize: '0.75rem',
                    letterSpacing: '0.01em'
                  }}
                >
                  Papers ranked by relevance with AI-generated analysis
                </Typography>

                <Collapse in={resultsVisible}>
                  <Box className="results-container" sx={{ ml: 0.5 }}>
                    {getCurrentPageItems().map((result, index) => {
                      const actualIndex = index + (currentPage * itemsPerPage);
                      const isExpanded = activeRankings.includes(actualIndex);

                      return (
                        <Card
                          key={`paper-${actualIndex}`}
                          className="debate-analysis-card"
                          sx={{
                            mb: 1.5,
                            borderLeft: `4px solid ${getScoreColor(result.score)}`,
                            bgcolor: isDarkMode ? 'rgba(18, 18, 18, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              transform: 'translateY(-1px)'
                            },
                            overflow: 'hidden'
                          }}
                          onClick={() => toggleResultExpand(actualIndex)}
                        >
                          <CardContent sx={{ p: '12px 16px 8px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Typography
                                variant="subtitle2"
                                component="div"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  lineHeight: 1.4,
                                }}
                              >
                                {extractPaperTitle(result)}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleResultExpand(actualIndex);
                                }}
                                sx={{
                                  ml: 0.5,
                                  width: '24px',
                                  height: '24px',
                                  transition: 'transform 0.3s ease',
                                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'
                                }}
                              >
                                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              </IconButton>
                            </Box>

                            <Box
                              className="relevance-score"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  mr: 1,
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              >
                                Relevance:
                              </Typography>
                              <Box
                                className="score-bar-container"
                                sx={{
                                  flexGrow: 1,
                                  height: '6px',
                                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                                  borderRadius: '3px',
                                  overflow: 'hidden',
                                  position: 'relative'
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${formatScore(result.score)}%`,
                                    borderRadius: '3px',
                                    background: `linear-gradient(90deg, ${getScoreColor(result.score)}${isDarkMode ? 'cc' : '99'}, ${getScoreColor(result.score)})`,
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)'
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  ml: 1,
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  color: getScoreColor(result.score)
                                }}
                              >
                                {formatScore(result.score)}%
                              </Typography>
                            </Box>
                            <Collapse in={isExpanded} timeout={300}>
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    className="arguments-box arguments-for"
                                    sx={{
                                      p: 2,
                                      bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(76, 175, 80, 0.2)'
                                    }}
                                  >
                                    <Typography
                                      className="arguments-title"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontWeight: 600,
                                        color: isDarkMode ? '#81c784' : '#2e7d32',
                                        mb: 1
                                      }}
                                    >
                                      <ThumbUp fontSize="small" sx={{ mr: 1 }} /> Arguments For
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)'
                                      }}
                                    >
                                      {extractArguments(result)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Box
                                    className="arguments-box arguments-against"
                                    sx={{
                                      p: 2,
                                      bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                                      borderRadius: '8px',
                                      border: '1px solid rgba(244, 67, 54, 0.2)'
                                    }}
                                  >
                                    <Typography
                                      className="arguments-title"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontWeight: 600,
                                        color: isDarkMode ? '#e57373' : '#c62828',
                                        mb: 1
                                      }}
                                    >
                                      <ThumbDown fontSize="small" sx={{ mr: 1 }} /> Arguments Against
                                    </Typography>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)'
                                      }}
                                    >
                                      {extractArgumentsAgainst(result)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                {getRelevantQuotes(result).length > 0 && (
                                  <Grid item xs={12} sx={{ mt: 1 }}>
                                    <Box
                                      className="relevant-quotes"
                                      sx={{
                                        mt: 1,
                                        p: 2,
                                        bgcolor: isDarkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(33, 150, 243, 0.2)',
                                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.05)'
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          mb: 1.5,
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: isDarkMode ? '#64b5f6' : '#1565c0'
                                        }}
                                      >
                                        <ChatBubbleOutline fontSize="small" sx={{ mr: 1 }} />
                                        Key Quotes
                                      </Typography>
                                      <Box sx={{
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        pr: 1
                                      }}>
                                        {getRelevantQuotes(result).map((quote, i) => (
                                          <Box
                                            key={i}
                                            sx={{
                                              p: 1,
                                              borderLeft: `3px solid ${theme.palette.primary.main}`,
                                              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)',
                                              borderRadius: '0 4px 4px 0',
                                              mb: 1.5
                                            }}
                                          >
                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                              "{quote}"
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Collapse>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>

                  {/* Pagination Controls */}
                  {rankingResults.length > itemsPerPage && (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 3,
                      mb: 1
                    }}>
                      <IconButton
                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={currentPage === 0}
                        sx={{
                          color: theme.palette.primary.main,
                          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                          '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                          },
                          '&.Mui-disabled': {
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
                          }
                        }}
                      >
                        <NavigateBeforeRounded />
                      </IconButton>

                      <Typography sx={{ mx: 2, color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)' }}>
                        Page {currentPage + 1} of {Math.ceil(rankingResults.length / itemsPerPage)}
                      </Typography>

                      <IconButton
                        onClick={() => setCurrentPage(Math.min(Math.ceil(rankingResults.length / itemsPerPage) - 1, currentPage + 1))}
                        disabled={currentPage >= Math.ceil(rankingResults.length / itemsPerPage) - 1}
                        sx={{
                          color: theme.palette.primary.main,
                          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                          '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                          },
                          '&.Mui-disabled': {
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
                          }
                        }}
                      >
                        <NavigateNextRounded />
                      </IconButton>
                    </Box>
                  )}
                </Collapse>
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>
    </motion.div>
  );
};

export default SearchProgress;
